import { useState, useCallback } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useSimpleRegistrationContext } from "pages/SimpleRegistrationsPage";
import { useApi } from "providers/ApiProvider";
import { useSnackbarContext } from "~/SnackbarProvider_v2";
import { LoadingBackground } from "components/SimpleRegistrations/LoadingBackground";
import { LeftSection } from "components/SimpleRegistrations/landscape/LeftSection";
import { RightSection } from "components/SimpleRegistrations/landscape/RightSection";
import { TopSection } from "components/SimpleRegistrations/portrait/TopSection";
import { BottomSection } from "components/SimpleRegistrations/portrait/BottomSection";
import { SimpleRegistrationsCompleteModal } from "~/modals/SimpleRegistrationsCompleteModal";
import { getBirthday } from "utils/birthdayUtil";

const Container = styled.div<{ isLandscape?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  ${({ isLandscape }) =>
    !isLandscape &&
    css`
      flex-direction: column;
    `}
  justify-content: center;
  align-items: center;
`;

export const SimpleRegistrations = () => {
  const {
    isLandscape,
    loading,
    setLoading,
    phoneNumber,
    birthday,
    name,
    acquisitionChannelsIds,
    smsEnabled,
    eventSmsEnabled,
    reset,
    visitPurpose,
    customerId,
  } = useSimpleRegistrationContext();
  const { customersApi, registrationApi } = useApi();
  const snackbar = useSnackbarContext();
  const [openCompleteModal, setOpenCompleteModal] = useState(false);

  const getSex = () => {
    const ssnHead = birthday.charAt(birthday.length - 1);
    return Number(ssnHead) % 2 ? "male" : "female";
  };

  const onSaveNewRegistration = useCallback(async () => {
    setLoading(true);
    try {
      if (customerId) {
        const res = await customersApi.editCustomer(customerId, {
          phoneNumber,
          birthday: getBirthday(birthday),
          sex: getSex(),
          name,
          acquisitionChannels: acquisitionChannelsIds,
          smsEnabled,
          eventSmsEnabled,
          ssnHead: birthday,
        });
        const response = await res.data;
        if (response.data.id) {
          await registrationApi.createRegistrationRequests({
            customerId: response.data.id,
            purpose:
              visitPurpose.length > 0
                ? visitPurpose.map((item) => item.name).join(",")
                : null,
          });
        }
      } else {
        const res = await customersApi.createCustomer({
          phoneNumber,
          birthday: getBirthday(birthday),
          sex: getSex(),
          name,
          acquisitionChannels: acquisitionChannelsIds,
          smsEnabled,
          eventSmsEnabled,
          ssnHead: birthday,
        });
        const response = await res.data;
        if (response.data.id) {
          await registrationApi.createRegistrationRequests({
            customerId: response.data.id,
            purpose:
              visitPurpose.length > 0
                ? visitPurpose.map((item) => item.name).join(",")
                : null,
          });
        }
      }
      setOpenCompleteModal(true);
    } catch (e) {
      snackbar.alert("실패했습니다.");
      reset();
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    phoneNumber,
    birthday,
    name,
    acquisitionChannelsIds,
    smsEnabled,
    eventSmsEnabled,
    visitPurpose,
    customerId,
  ]);

  return (
    <>
      {loading && <LoadingBackground />}
      {openCompleteModal && (
        <SimpleRegistrationsCompleteModal
          open={openCompleteModal}
          onClose={() => {
            reset();
            setOpenCompleteModal(false);
          }}
        />
      )}
      <Container isLandscape={isLandscape}>
        {isLandscape ? (
          <>
            <LeftSection />
            <RightSection
              onSaveNewRegistration={onSaveNewRegistration}
              setOpenCompleteModal={(flag) => {
                setOpenCompleteModal(flag);
              }}
            />
          </>
        ) : (
          <>
            <TopSection />
            <BottomSection
              onSaveNewRegistration={onSaveNewRegistration}
              setOpenCompleteModal={(flag) => {
                setOpenCompleteModal(flag);
              }}
            />
          </>
        )}
      </Container>
    </>
  );
};
